// ===========================================
// Ticket money summary (events Release B, #681)
// ===========================================
// The reconcile block at the top of an event's Tickets tab: tickets, booking fees, card (processing)
// fees, what buyers were charged and what was refunded, all from the per-checkout money records.
// Tickets bought before those records existed are NOT in the figures: they are counted and named,
// never shown as a zero.

import { Alert, Paper, Skeleton, Stack, Typography } from '@mui/material';
import type { EventTicketSummary } from '../../api/events.api';
import { formatCurrencyFromCents } from '../../utils/currency';

interface Props {
  summary: EventTicketSummary | undefined;
  isLoading: boolean;
  isError: boolean;
  /** False when the summary is not wanted (no `events.read`, or a free event): the block renders nothing. */
  enabled?: boolean;
}

function Figure({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 130 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6" data-testid={testId}>
        {value}
      </Typography>
    </Stack>
  );
}

/**
 * Seats (events B3b, #687): sold (PAID or CHECKED_IN), held by open checkouts, and the capacity; an oversell
 * (a buyer who paid after their hold lapsed is never refused) is named, never hidden.
 */
function SeatsLine({ seats, holdMinutes }: { seats: EventTicketSummary['seats']; holdMinutes: number }) {
  if (seats.capacity === null && seats.sold === 0 && seats.held === 0) return null;
  return (
    <Stack spacing={1} sx={{ mb: 1 }} data-testid="ts-seats">
      <Typography variant="body2">
        Seats: {seats.sold} sold
        {/* The figure is the server's (the hold plus its grace), never a literal here (Release B /qa L1). */}
        {seats.held > 0 ? `, ${seats.held} held by open checkouts (freed within ${holdMinutes} minutes if not paid)` : ''}
        {seats.capacity === null ? ', no limit' : ` of ${seats.capacity}`}
      </Typography>
      {seats.oversold > 0 && (
        <Alert severity="warning" data-testid="ts-oversold">
          {seats.oversold} more {seats.oversold === 1 ? 'seat is' : 'seats are'} sold than the capacity. Buyers who paid after their
          hold lapsed are never refused automatically; contact them or raise the capacity.
        </Alert>
      )}
    </Stack>
  );
}

export default function TicketSummaryBlock({ summary, isLoading, isError, enabled = true }: Props) {
  // A disabled query (the caller may not read it, or the event is free) is not an error: show nothing.
  if (!enabled) return null;
  if (isLoading) return <Skeleton variant="rounded" height={88} sx={{ mb: 2 }} data-testid="ticket-summary-loading" />;
  if (isError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        The ticket money summary could not be loaded.
      </Alert>
    );
  }
  if (!summary) return null;
  const missing = summary.ticketsWithoutCheckoutRecord;
  if (summary.checkoutCount === 0 && missing === 0) {
    return (
      <Paper sx={{ p: 2, mb: 2 }} aria-label="Ticket money summary" data-testid="ticket-summary-empty">
        <SeatsLine seats={summary.seats} holdMinutes={summary.holdFreedWithinMinutes} />
        <Typography variant="body2" color="text.secondary">
          No paid tickets yet.
        </Typography>
      </Paper>
    );
  }
  return (
    <Paper sx={{ p: 2, mb: 2 }} aria-label="Ticket money summary" data-testid="ticket-summary">
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Money summary
      </Typography>
      <SeatsLine seats={summary.seats} holdMinutes={summary.holdFreedWithinMinutes} />
      <Stack direction="row" spacing={3} useFlexGap flexWrap="wrap">
        {/* Every figure below INCLUDES refunded orders; Refunded and Net show what went back. */}
        <Figure label="Tickets (incl. refunded)" value={String(summary.ticketCount)} testId="ts-tickets" />
        <Figure label="Ticket sales (incl. refunded)" value={formatCurrencyFromCents(summary.ticketSubtotalCents)} testId="ts-sales" />
        <Figure label="Booking fees (incl. refunded)" value={formatCurrencyFromCents(summary.bookingFeeTotalCents)} testId="ts-booking" />
        <Figure
          label="Card fees paid by families"
          value={formatCurrencyFromCents(summary.processingFeeTotalCents)}
          testId="ts-processing"
        />
        <Figure label="Charged (incl. refunded)" value={formatCurrencyFromCents(summary.chargedCents)} testId="ts-charged" />
        <Figure
          label={`Refunded (${summary.refundedCheckoutCount} ${summary.refundedCheckoutCount === 1 ? 'order' : 'orders'})`}
          value={formatCurrencyFromCents(summary.refundedCents)}
          testId="ts-refunded"
        />
        <Figure label="Net after refunds" value={formatCurrencyFromCents(summary.chargedCents - summary.refundedCents)} testId="ts-net" />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Card fees are only what families paid when the school passes them on; a school that absorbs them shows $0.00 here.
      </Typography>
      {missing > 0 && (
        <Alert severity="info" sx={{ mt: 2 }} data-testid="ts-missing">
          {missing} {missing === 1 ? 'ticket was' : 'tickets were'} bought before these figures were recorded, so{' '}
          {missing === 1 ? 'it is' : 'they are'} not included above.
        </Alert>
      )}
    </Paper>
  );
}
